import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CARE_URL } from "@/lib/sites";
import { Pagination, usePagination } from "@/components/Pagination";
import { BuyAgainButton } from "@/components/BuyAgain";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CustomerProfile from "@/pages/CustomerProfile";
import MyPets from "@/pages/MyPets";
import Wishlist from "@/pages/Wishlist";
import Help from "@/pages/Help";
import { useSeo } from "@/lib/seo";

/** My account: one page with tabs instead of five pages (orders, pets,
 *  wishlist, profile, help). "Orders and bookings" lists shop orders, care
 *  bookings and donations together, newest first, with the next step on each
 *  row; the Shop orders and Subscriptions views keep every order action. */

const TABS = [["orders", "Orders and bookings"], ["pets", "My pets"], ["wishlist", "Wishlist"], ["profile", "Profile and security"], ["help", "Help"]];
const VIEWS = [["all", "All"], ["shop", "Shop orders"], ["subs", "Subscriptions"], ["care", "Care bookings"], ["donation", "Donations"]];
const PAGE_SIZE = 10;

export default function Account() {
  const [sp, setSp] = useSearchParams();
  const tab = TABS.some(([k]) => k === sp.get("tab")) ? sp.get("tab") : "orders";
  const set = (patch) => {
    const n = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setSp(n, { replace: true });
  };
  useSeo({ title: "My account", path: "/account" });
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">My account</h1>
      <nav aria-label="My account" className="mt-4 flex gap-1 overflow-x-auto border-b border-stone-200 [scrollbar-width:none]" data-testid="account-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => set({ tab: k === "orders" ? null : k, view: null, section: null })} aria-current={tab === k ? "page" : undefined}
            data-testid={`account-tab-${k}`}
            className={`shrink-0 px-3 sm:px-4 py-2.5 text-sm font-semibold border-b-[3px] -mb-px ${tab === k ? "border-marigold text-stoneWarm" : "border-transparent text-stone-500 hover:text-stoneWarm"}`}>{label}</button>
        ))}
      </nav>
      <div className="pt-5">
        {tab === "orders" && <Activity view={VIEWS.some(([k]) => k === sp.get("view")) ? sp.get("view") : "all"} setView={(v) => set({ view: v === "all" ? null : v })} />}
        {tab === "pets" && <MyPets embedded />}
        {tab === "wishlist" && <Wishlist embedded />}
        {tab === "profile" && <CustomerProfile embedded section={sp.get("section")} />}
        {tab === "help" && <Help embedded />}
      </div>
    </div>
  );
}

const SHOP_STATUS = {
  received: ["Placed", "busy"], accepted: ["Being packed", "busy"], preparing: ["Being packed", "busy"], ready_for_pickup: ["Being packed", "busy"],
  out_for_delivery: ["On the way", "busy"], picked_up: ["On the way", "busy"], delivered: ["Delivered", "done"],
  cancelled: ["Cancelled", "off"], refunded: ["Refunded", "off"], rejected: ["Cancelled", "off"],
};
const CARE_STATE = { confirmed: "soon", pending_payment: "soon", in_progress: "busy", completed: "done", cancelled: "off", no_show: "off" };
const STATE_CLS = { busy: "text-[#0F6E68] font-semibold", soon: "text-[#8A4B00] font-semibold", done: "text-stone-600", off: "text-terracotta" };
const KIND_CLS = { shop: "bg-[#FFF1DC] text-[#8A4B00]", care: "bg-[#E3F4F2] text-[#0F6E68]", donation: "bg-[#EAF1DF] text-[#3F6B2A]" };
const KIND_LABEL = { shop: "Shop", care: "Care", donation: "Donation" };
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "");
const rupees = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;
const DATES = [["90", "Last 3 months"], ["365", "This year"], ["all", "All time"]];
const STATES = [["any", "Any status"], ["busy", "On the way or in progress"], ["soon", "Upcoming"], ["done", "Delivered or done"], ["off", "Cancelled"]];

function rowsFrom(orders, bookings) {
  const shop = orders.map((o) => {
    const [label, state] = SHOP_STATUS[o.status] || [o.status?.replace(/_/g, " ") || "", "busy"];
    const names = (o.items || []).map((i) => i.name).filter(Boolean);
    const kind = o.donation ? "donation" : "shop";
    return {
      id: o.id, kind, at: o.created_at, state, label,
      title: o.donation ? `${o.donation.need_title || "Donation"} for ${o.donation.ngo_name || "an NGO"}` : `${names.slice(0, 1).join("") || "Order"}${names.length > 1 ? ` and ${names.length - 1} more` : ""}`,
      meta: `${o.vendor_name || ""} · ${day(o.created_at)} · #${o.id.slice(0, 8).toUpperCase()}`,
      amount: o.total, order: o,
    };
  });
  const care = bookings.map((b) => ({
    id: b.id, kind: "care", at: b.start_at || b.created_at, state: CARE_STATE[b.status] || "soon", label: b.status_label || b.status,
    title: `${b.service?.name || "Care visit"}${b.pet?.name ? ` for ${b.pet.name}` : ""}`,
    meta: `${b.salon_name || ""} · ${day(b.start_at || b.date)}${b.start ? `, ${b.start}` : ""}${b.visit?.mode === "pickup" ? " · pickup and drop" : b.visit?.mode === "home" ? " · home visit" : ""}`,
    amount: b.amount, booking: b,
  }));
  return [...shop, ...care].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
}

function Activity({ view, setView }) {
  const [orders, setOrders] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [state, setState] = useState("any");
  const [range, setRange] = useState("90");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => {
    api.get("/customer/orders").then(({ data }) => setOrders(Array.isArray(data) ? data : []), () => setOrders([]));
    api.get("/care/me/bookings").then(({ data }) => setBookings(Array.isArray(data) ? data : []), () => setBookings([]));
  }, []);
  const rows = useMemo(() => {
    if (!orders) return [];
    const since = range === "all" ? "" : new Date(Date.now() - Number(range) * 86400000).toISOString();
    const word = q.trim().toLowerCase();
    return rowsFrom(orders, bookings).filter((r) => (view === "all" || r.kind === view)
      && (state === "any" || r.state === state) && (!since || (r.at || "") >= since)
      && (!word || `${r.title} ${r.meta}`.toLowerCase().includes(word)));
  }, [orders, bookings, view, state, range, q]);
  useEffect(() => { setPage(1); }, [view, state, range, q]);
  const { pageCount, page: p, paged } = usePagination(rows, PAGE_SIZE, page, setPage);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2" data-testid="activity-filters">
        <div className="flex gap-0.5 rounded-full bg-stone-100 p-1 overflow-x-auto max-w-full [scrollbar-width:none]">
          {VIEWS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setView(k)} aria-pressed={view === k} data-testid={`activity-view-${k}`}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${view === k ? "bg-white font-bold shadow-sm" : "text-stone-600"}`}>{label}</button>
          ))}
        </div>
        {!["shop", "subs"].includes(view) && (
          <>
            <select aria-label="Status" value={state} onChange={(e) => setState(e.target.value)} className="h-9 rounded-full border border-stone-300 bg-white px-3 text-sm">
              {STATES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select aria-label="When" value={range} onChange={(e) => setRange(e.target.value)} className="h-9 rounded-full border border-stone-300 bg-white px-3 text-sm">
              {DATES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input aria-label="Search orders and bookings" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by product, shop or provider"
              className="h-9 flex-1 min-w-0 sm:min-w-[14rem] rounded-full border border-stone-300 bg-white px-4 text-sm" />
          </>
        )}
      </div>

      {view === "shop" || view === "subs" ? (
        <div className="mt-4"><CustomerDashboard embedded initialTab={view === "subs" ? "subs" : "orders"} key={view} /></div>
      ) : orders === null ? (
        <div className="mt-4 space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-2xl bg-stone-100 animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-stone-300 bg-white p-8 text-center text-stone-600" data-testid="activity-empty">
          {orders.length || bookings.length ? "Nothing matches these filters." : (
            <>No orders or bookings yet. <Link to="/shop" className="font-semibold text-teal hover:underline">Start shopping</Link> or <a href={`${CARE_URL}/services`} className="font-semibold text-teal hover:underline">book care</a>.</>
          )}
        </div>
      ) : (
        <>
          <ul className="mt-4 rounded-2xl border border-stone-200 bg-white divide-y divide-stone-100 overflow-hidden" data-testid="activity-list">
            {paged.map((r) => (
              <li key={`${r.kind}-${r.id}`} className="px-4 py-3 grid grid-cols-[auto_minmax(0,1fr)_auto] sm:grid-cols-[6.5rem_minmax(0,1fr)_10rem_6rem_auto] gap-x-3 gap-y-1 items-center" data-testid={`activity-${r.kind}-${r.id}`}>
                <span className={`justify-self-start rounded-full px-2.5 py-0.5 text-xs font-bold ${KIND_CLS[r.kind]}`}>{KIND_LABEL[r.kind]}</span>
                <div className="min-w-0 col-span-2 sm:col-span-1 row-start-2 sm:row-start-auto">
                  <div className="font-semibold truncate">{r.title}</div>
                  <div className="text-xs text-stone-500 truncate">{r.meta}</div>
                </div>
                <span className={`text-sm ${STATE_CLS[r.state]} sm:justify-self-start justify-self-end col-start-2 sm:col-start-auto row-start-1 sm:row-start-auto text-right sm:text-left`}>{r.label}</span>
                <span className="hidden sm:block text-right font-bold tabular-nums">{rupees(r.amount)}</span>
                <span className="justify-self-end row-start-2 sm:row-start-auto col-start-3 sm:col-start-auto"><RowAction r={r} /></span>
              </li>
            ))}
          </ul>
          <Pagination page={p} pageCount={pageCount} onChange={setPage} total={rows.length} pageSize={PAGE_SIZE} testidPrefix="activity-pg" />
        </>
      )}
    </div>
  );
}

function RowAction({ r }) {
  const cls = "inline-flex items-center rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold hover:border-stoneWarm whitespace-nowrap";
  if (r.kind === "care") return <a href={`${CARE_URL}/bookings/${r.id}`} className={cls}>{r.state === "soon" && r.booking?.can_reschedule ? "Reschedule" : "Details"}</a>;
  if (r.state === "busy") return <Link to={`/orders/${r.id}/track`} className={cls}>Track</Link>;
  if (r.kind === "shop" && ["done", "off"].includes(r.state) && !r.order?.source) return <BuyAgainButton orderId={r.id} label="Buy again" className="!h-8 !px-3 !text-xs" />;
  return <Link to="/account?view=shop" className={cls}>Details</Link>;
}
